import { useCallback, useContext, useRef, useState } from 'react';
import { ClickAwayListener, makeStyles, Table, TableBody, TableCell, TableRow, Tooltip, Zoom } from '@material-ui/core';
import moment from 'moment';
import useMobile from '../../utils/useMobile';
import { dutyAssignAPI } from '../../apis';
import { DutyContext } from '../../page/DutyAssign';
import { onKeyUpEnterOrSpace } from '../../utils';

const useStylesLongremarkTooltip = makeStyles((theme) => ({
  arrow: {
    color: '#FFF',
    '&::before': {
      boxShadow: '0 0 0.5rem 0 rgba(125, 134, 169, 0.2)',
    },
  },
  tooltip: () => ({
    borderRadius: '0.625rem',
    boxShadow: '0 0 0.5rem 0 rgba(125, 134, 169, 0.2)',
    backgroundColor: '#FFF2C1',
    padding: '0.5rem 0.25rem',
    color: theme.palette.font.white,
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    fontWeight: '500',
    whiteSpace: 'pre-line',
    maxWidth: '100vw',
  }),
}));

const useStyles = makeStyles(() => ({
  markiconBtn: {
    borderRadius: '4px',
    padding: '0',
    marginLeft: '0.625rem',
    width: '1.25rem',
    height: '1.25rem',
    background: '#AAAAAA',
    color: '#FFF',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  markicon: {
    fontSize: '1.25rem',
    color: '#c0c6cb',
  },
  table: {
    borderSpacing: '0',
    borderCollapse: 'collapse',
    '& .MuiTableCell-body': {
      verticalAlign: 'middle',
      padding: '0.25rem',
      color: '#2E4469',
      fontSize: '0.75rem',
      fontFamily: 'NotoSans-Medium',
    },
    '& .MuiTableCell-root': {
      border: 'none',
      width: '7.2rem',
    },
    '& .MuiTableRow-root': {
      '&>td:first-child': {
        width: '4rem',
        textAlign: 'right',
      },
    },
  },
  borderRow: {
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #E6E7E8',
    },
    '&>td:first-child': {
      borderBottom: 'none',
    },
  },
  highlight: {
    '&.MuiTableCell-root': {
      background: '#FECA06',
      // background: '#FFEFEF',
      // color: '#F34032',
    },
  },
  cellBody: {
    width: '7.2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const WeeklyTable = ({ data, currentDate, isRosterName }) => {
  const classes = useStyles();
  const result = (assignItem) =>
    isRosterName === 1 ? `${assignItem.rosterCode}-${assignItem.areaName}` : assignItem.areaName;
  const getHighlightClassName = useCallback(
    (date) => {
      return date === currentDate ? classes.highlight : '';
    },
    [currentDate, classes.highlight]
  );

  return (
    <Table className={classes.table}>
      <TableBody>
        <TableRow className={classes.borderRow}>
          <TableCell align="center">Date</TableCell>
          {data.map((item) => (
            <TableCell className={getHighlightClassName(item.date)} key={item.date} align="center">
              {moment(item.date, 'YYYY-MM-DD').format('DD-MMM')}
            </TableCell>
          ))}
        </TableRow>
        <TableRow className={classes.borderRow}>
          <TableCell align="center">SRS</TableCell>
          {data.map((item) => (
            <TableCell className={getHighlightClassName(item.date)} key={item.date} align="center">
              {item.type.map((assignItem, index) => (
                <div className={classes.cellBody} key={assignItem}>
                  {assignItem}
                </div>
              ))}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell align="center">J/T Asg</TableCell>
          {data.map((item) => (
            <TableCell className={getHighlightClassName(item.date)} key={item.date} align="center">
              {item.assgin.map((assignItem, index) => (
                <div className={classes.cellBody} key={assignItem}>
                  {`${result(assignItem)}`}
                </div>
              ))}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );
};

const Weekly = ({ placement, id, Rinfo }) => {
  const [isPad] = useMobile();
  const longremarkclasses = useStylesLongremarkTooltip();
  const classes = useStyles();
  const [toolTipOpen, setToolTipOpen] = useState(false);
  const [data, setData] = useState([]);
  const ref = useRef(null);
  const { rosterId, isRosterName } = useContext(DutyContext);
  const [currentDate, setCurrentDate] = useState(moment().format('YYYY-MM-DD'));

  const getWeekly = useCallback(async () => {
    try {
      const dutyDateInfo = JSON.parse(sessionStorage.getItem('dutyInfo'));
      const rosterDate = dutyDateInfo?.date ?? moment();
      const { result } = await dutyAssignAPI.weekly(
        { ...Rinfo, date: moment(rosterDate)?.format('YYYY-MM-DD'), dutyTemplateRosterId: rosterId },
        { isHideLoading: true }
      );
      if (ref.current.flag) {
        setData(result);
        setToolTipOpen(true);
        setCurrentDate(moment(rosterDate).format('YYYY-MM-DD'));
      }
    } catch (error) {
      console.log(error);
    }
  }, [rosterId]);

  const handleTooltipClose = (event) => {
    event.stopPropagation();
    ref.current.flag = false;
    setToolTipOpen(false);
  };

  const handleTooltipOpen = (event) => {
    event.stopPropagation();
    ref.current.flag = true;
    getWeekly();
  };

  return isPad ? (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        id={id}
        classes={longremarkclasses}
        title={<WeeklyTable data={data} currentDate={currentDate} isRosterName={isRosterName} />}
        arrow
        TransitionComponent={Zoom}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        onClose={handleTooltipClose}
        open={toolTipOpen}
      >
        <div
          ref={ref}
          className={classes.markiconBtn}
          onClick={handleTooltipOpen}
          tabIndex={0}
          onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleTooltipOpen)}
        >
          R
        </div>
      </Tooltip>
    </ClickAwayListener>
  ) : (
    <Tooltip
      ref={ref}
      id={id}
      classes={longremarkclasses}
      title={<WeeklyTable data={data} currentDate={currentDate} isRosterName={isRosterName} />}
      arrow
      TransitionComponent={Zoom}
      placement={placement}
      onOpen={handleTooltipOpen}
      onClose={handleTooltipClose}
      open={toolTipOpen}
      enterDelay={1000}
    >
      <div className={classes.markiconBtn}>R</div>
    </Tooltip>
  );
};

export default Weekly;
